import { bossDeathAge } from './defeat';
/**
 * Plan 07 — skeletal prototype: the BOSS as a real rigged actor (all tiers; one skeleton, one
 * skinned mesh — cheaper than the merged-mesh approximation it replaces, which bent the whole
 * body to fake a club swing).
 *
 * Clips (monster profile): idle (breathing sway) + slam (primary-attack, phase-mapped from the
 * sim slam timers, the same envelope boundaries clips.test.ts pins continuity for) + hit (flinch from the
 * bossHitAt telemetry) + death (collapse driven by wonAt, then a held corpse for BOSS_DEATH_HOLD
 * seconds — the procedural Boss vanished at 'won'; the death clip needs the body to stay).
 *
 * The root transform stays where Boss put it (root node at spec.z, telegraph ground untouched —
 * gameplay contract); bones carry all motion. Purity: pose = f(snapshot) only (P5.3 discipline).
 */
import { Constants, Color3, MeshBuilder, TransformNode } from '@babylonjs/core/pure';
import type { Mesh, PBRMaterial, Scene, StandardMaterial } from '@babylonjs/core/pure';
import type { RenderMaterials } from './scene';
import { applyBossGlow, buildBoss } from './actors';
import { BRIDGE_HALF_WIDTH } from './world';
import { bossPartBone, RIG_BOSS } from './anim/rigs';
import {
  BOSS_CLIP_DEATH,
  BOSS_CLIP_HIT,
  BOSS_CLIP_IDLE,
  BOSS_CLIP_SLAM,
  BOSS_DEATH_DURATION,
  bossPoseInto,
  compileClip,
  createPose,
  type Pose,
} from './anim/clips';
import { applyPose, bindRigidSkin, buildSkeleton, type SkeletonHandle } from './anim/skeletal';
import type { SimState } from '../sim/sim';
import type { LevelSpec } from '../sim/level';

/** How long the fallen boss stays on the bridge after the death clip ends (s). */
export const BOSS_DEATH_HOLD = 4;

export class SkeletalBoss {
  readonly root: TransformNode;
  private readonly body: Mesh;
  private readonly telegraph: Mesh;
  private readonly shockwave: Mesh;
  private readonly handle: SkeletonHandle;
  private readonly material: PBRMaterial;
  private readonly pose: Pose;
  private readonly scratch: Pose;
  private readonly scratch2: Pose;
  private readonly compiled = {
    idle: compileClip(RIG_BOSS, BOSS_CLIP_IDLE),
    slam: compileClip(RIG_BOSS, BOSS_CLIP_SLAM),
    hit: compileClip(RIG_BOSS, BOSS_CLIP_HIT),
    death: compileClip(RIG_BOSS, BOSS_CLIP_DEATH),
  };

  constructor(scene: Scene, materials: RenderMaterials, private readonly spec: LevelSpec['boss']) {
    this.root = new TransformNode('boss-root', scene);
    const geometry = buildBoss(scene, materials.boss as PBRMaterial, 'boss-torso');
    this.body = geometry.mesh;
    this.material = materials.boss as PBRMaterial;
    this.body.name = 'boss-torso'; // probe watch name (inspect.mjs asserts on it)
    this.handle = buildSkeleton(scene, RIG_BOSS, 'boss');
    bindRigidSkin(this.body, this.handle, geometry.parts, RIG_BOSS, bossPartBone);
    this.body.parent = this.root;
    this.root.position.set(0, 0, spec.z);

    // Plan 13: the zone reaches 3 m behind the standoff, under the squad's footprint outline, so a
    // landing slam reads as "the red touches the outline".
    this.telegraph = MeshBuilder.CreateGround('telegraph', { width: BRIDGE_HALF_WIDTH * 2, height: spec.standoff + 4 }, scene);
    this.telegraph.material = materials.glow(new Color3(1, 0.12, 0.05), 0.32);
    this.telegraph.position.set(0, 0.06, spec.z - spec.standoff / 2 - 1);
    this.telegraph.setEnabled(false);
    (this.telegraph.material as StandardMaterial).alphaMode = Constants.ALPHA_ADD;

    this.shockwave = MeshBuilder.CreateTorus('slam-shockwave', { diameter: 0.955, thickness: 0.045, tessellation: 32 }, scene);
    this.shockwave.material = materials.glow(new Color3(1, 0.36, 0.12), 0.8);
    (this.shockwave.material as StandardMaterial).alphaMode = Constants.ALPHA_ADD;
    this.shockwave.position.set(0, 0.09, this.telegraph.position.z);
    this.shockwave.setEnabled(false);

    this.pose = createPose(RIG_BOSS);
    this.scratch = createPose(RIG_BOSS);
    this.scratch2 = createPose(RIG_BOSS);
  }

  /**
   * Sync from the snapshot (pure — `dt` is accepted for call-site parity and unused by contract).
   * Visible during 'boss' AND for the death-hold window after 'won', so the death clip plays out.
   */
  update(sim: SimState, _dt: number, presentationAge?: number): void {
    const deathAge = bossDeathAge(sim, presentationAge);
    const corpse = deathAge !== null && deathAge <= BOSS_DEATH_DURATION + BOSS_DEATH_HOLD;
    const active = sim.phase === 'boss' || (sim.phase === 'won' && corpse);
    this.root.setEnabled(active);
    if (!active) {
      this.telegraph.setEnabled(false);
      this.shockwave.setEnabled(false);
      return;
    }
    bossPoseInto(sim, this.spec, this.compiled, this.pose, this.scratch, this.scratch2, presentationAge);
    applyPose(this.handle, this.pose);
    applyBossGlow(this.material, sim, this.spec);

    const impactAge = sim.slamAt === null ? -1 : sim.t - sim.slamAt;
    const showImpact = sim.phase === 'boss' && sim.slam !== null && impactAge >= 0 && impactAge < 0.4;
    this.shockwave.setEnabled(showImpact);
    if (showImpact && sim.slam) {
      const [lo, hi] = sim.slam.zone;
      const expansion = 0.35 + 0.65 * impactAge / 0.4;
      this.shockwave.position.x = ((lo + hi) / 2) * BRIDGE_HALF_WIDTH;
      this.shockwave.scaling.set((hi - lo) * BRIDGE_HALF_WIDTH * expansion, 1, (this.spec.standoff + 4) * expansion);
      (this.shockwave.material as StandardMaterial).alpha = 0.8 * (1 - impactAge / 0.4);
    }

    if (sim.slam && sim.phase === 'boss') {
      const show = sim.slam.active === 'telegraph';
      this.telegraph.setEnabled(show);
      if (show) {
        const [lo, hi] = sim.slam.zone;
        const width = (hi - lo) * BRIDGE_HALF_WIDTH;
        this.telegraph.scaling.x = Math.max(0.2, width / (BRIDGE_HALF_WIDTH * 2));
        this.telegraph.position.x = ((lo + hi) / 2) * BRIDGE_HALF_WIDTH;
        // Pulse driven by sim time, not a wall clock: pause-frozen like the rest of the boss.
        const pulse = 0.22 + 0.18 * Math.sin(sim.t * 18);
        (this.telegraph.material as StandardMaterial).alpha = pulse;
      }
    } else {
      this.telegraph.setEnabled(false);
    }
  }

  /** Test/probe handle: the pure pose for a snapshot (no side effects, no Babylon types). */
  debugPose(sim: SimState): Pose {
    const out = createPose(RIG_BOSS);
    bossPoseInto(sim, this.spec, this.compiled, out, createPose(RIG_BOSS), createPose(RIG_BOSS));
    return out;
  }

  /** Test/probe handle: bone facts (names + absolute bind positions ⇒ rig identity on the wire). */
  debugBones(): { name: string; y: number; z: number }[] {
    return this.handle.bones.map((bone) => ({
      name: bone.name,
      y: bone.getAbsoluteMatrix().m[13] ?? 0,
      z: bone.getAbsoluteMatrix().m[14] ?? 0,
    }));
  }
}
